import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useProperty, usePropertyStats, toggleProperty } from "@/_actions/propertiesActions";
import { NumericFormat } from "react-number-format";
import Flicking from "@egjs/react-flicking";
import "@egjs/react-flicking/dist/flicking.css";
import { usePlan } from "@/_actions/authActions";
import downloadPropertyPdf from "../../utils/downloadPdf";
import Notification from "@/components/overlays/Notification";
import { usePromoter } from "@/_actions/membershipActions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function PropertyDetail({
  id,
  title,
  status,
  mutateData,
  toggleModal,
  publishedProperties,
  activateProperty,
}) {
  const { data: propertyData } = useProperty(id);
  const { data: planData } = usePlan();
  const { plan } = planData || {};
  const { data: stats } = usePropertyStats(id);
  const [showNotification, toggleNotification] = useState(false);
  const [textNotification, setTextNotification] = useState("");
  const [showNotificationError, toggleNotificationError] = useState(false);
  const [textNotificationError, setTextNotificationError] = useState("");
  const [timeNotification, setTimeNotification] = useState(0);
  const { data: promoter } = usePromoter(propertyData?.membership);

  const navigate = useNavigate();

  const editPropertyStep = (id, step, singleSection = false) => {
    navigate(`edit/${id}`, { state: { step, singleSection } });
  };

  const handleToggleNotification = (text) => {
    setTextNotification(text);
    toggleNotification(true);
  };

  const handleToggleNotificationError = (text) => {
    setTextNotificationError(text);
    toggleNotificationError(true);
  };
  const planLimit = plan?.plan_addons?.properties_quantity;
  const hasLimit = typeof planLimit === "number";
  const isUnlimited = planLimit == -1;

  const reachedLimit = hasLimit && !isUnlimited && publishedProperties >= planLimit;

  const canEdit = !(status === "pending" && reachedLimit);

  const handleEdit = (step, singleSection) => {
    if (!canEdit) {
      window.alert("Has alcanzado el limite de propiedades activas.");
      return;
    }
    editPropertyStep(id, step, singleSection);
  };

  return (
    <section className="w-full py-4">
      <div className="bg-gray-200">
        <p className="p-3">{title}</p>
      </div>
      <div className="space-y-3">
        <div>
          <Flicking className="grid grid-cols-1 gap-3" align="prev" circular={true}>
            {propertyData?.images?.map((i) => (
              <img key={i?.url} src={i?.url} />
            ))}
          </Flicking>
        </div>
        <div className="text-center md:hidden space-y-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={!canEdit}
                className={`button-secondary mr-2 ${
                  !canEdit ? "cursor-not-allowed opacity-50" : ""
                }`}
                onClick={(e) => {
                  if (!canEdit) e.preventDefault();
                }}
              >
                Editar
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Editar Propiedad</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={!canEdit} onClick={() => handleEdit(1, false)}>
                Editar Todo
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canEdit} onClick={() => handleEdit(1, true)}>
                Información General
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canEdit} onClick={() => handleEdit(2, true)}>
                Amenidades
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canEdit} onClick={() => handleEdit(3, true)}>
                Ubicación
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canEdit} onClick={() => handleEdit(4, true)}>
                Fotografías
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {plan?.plan_addons?.properties_pdfs ? (
            <button
              className="button-secondary mr-2"
              onClick={() => {
                setTimeNotification(100000);
                handleToggleNotification("Descargando archivo. Puede tardar varios segundos.");
                downloadPropertyPdf(id)
                  .then(() => {
                    toggleNotification(false);
                    setTimeNotification(5000);
                    handleToggleNotification("Archivo descargado con éxito.");
                  })
                  .catch(() => {
                    toggleNotification(false);
                    handleToggleNotificationError("Ha ocurrido un error.");
                  });
              }}
            >
              Descargar PDF
            </button>
          ) : null}
          {status === "published" ? (
            <button
              onClick={() => {
                toggleProperty(
                  {
                    status: "inactive",
                    id: id,
                    confirm: true,
                  },
                  () => {
                    mutateData();
                    toggleModal(false);
                  },
                );
              }}
              className="button-secondary m-2"
            >
              Desactivar Propiedad
            </button>
          ) : null}
          {status === "pending" ? (
            <button disabled className="button-secondary mr-2">
              Desactivar Propiedad
            </button>
          ) : null}
          {status === "inactive" ? (
            <div className="has-tooltip">
              <button
                onClick={() => {
                  activateProperty({ ...propertyData, id: id });
                }}
                className="button-secondary mr-2"
              >
                Activar Propiedad
              </button>
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <span className="text-bold capitalize text-blue-cyt">ID: {id}</span>
          <div className="text-right">
            <span className="badge-green">
              {status === "published"
                ? "Publicado"
                : status === "pending"
                  ? "Pendiente"
                  : "Inactivo"}
            </span>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between">
            <p className="text-light text-gray-900">Promotor:</p>
            <div className="justify-end text-right">
              <p className="text-right font-bold">
                {propertyData?.contact_card?.name
                  ? propertyData?.contact_card?.name
                  : `${promoter?.first_name} ${promoter?.last_name}`}
              </p>
              <p className="text-left break-all">{promoter?.email}</p>
              <p className="text-right ">
                <NumericFormat
                  className="text-right"
                  format="## ## ## ## ## ## ## ##"
                  displayType="text"
                  value={promoter?.whatsapp || promoter?.phone_number}
                ></NumericFormat>
              </p>
            </div>
          </div>

          {propertyData?.price_rent ? (
            <div className="grid grid-cols-2">
              <p className="text-gray-900">Precio Renta:</p>
              <NumericFormat
                className="text-right"
                prefix="$"
                displayType="text"
                thousandSeparator={true}
                value={propertyData?.price_rent}
                suffix={propertyData?.currency_rent}
              ></NumericFormat>
            </div>
          ) : null}

          {propertyData?.price_sale ? (
            <div className="grid grid-cols-2">
              <p className="text-gray-900">Precio Venta: </p>
              <NumericFormat
                className="text-right"
                prefix="$"
                displayType="text"
                thousandSeparator={true}
                value={propertyData?.price_sale}
                suffix={propertyData?.currency_sale}
              ></NumericFormat>
            </div>
          ) : null}

          {propertyData?.price_transfer ? (
            <div className="grid grid-cols-2">
              <p className="text-gray-900">Precio Traspaso: </p>{" "}
              <NumericFormat
                className="text-right"
                prefix="$"
                displayType="text"
                thousandSeparator={true}
                value={propertyData?.price_transfer}
                suffix={propertyData?.currency_transfer}
              ></NumericFormat>
            </div>
          ) : null}
          <p className="border-t-2 border-gray-200 pt-4 text-blue-cyt">Estadísticas</p>
          <div className="grid grid-cols-2">
            {stats?.number_views ? (
              <div>
                <div className="grid-cols grid space-y-2">
                  <p className="">Número de visitas</p>
                  <p className="text-sm">{stats.number_views}</p>
                </div>
              </div>
            ) : null}
            {stats?.number_views ? (
              <div>
                <div className="grid-cols grid space-y-2">
                  <p>Número de contactos</p>
                  <p className="text-sm">{stats.number_contacts}</p>
                </div>
              </div>
            ) : null}
            {stats?.number_views ? (
              <div className="py-3">
                <div className="grid-cols grid space-y-2">
                  <p>CTR</p>
                  <p className="text-sm">{stats.ctr}</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <Notification
          open={showNotification}
          setOpen={toggleNotification}
          title={textNotification}
          timeOut={timeNotification}
        />
        <Notification
          open={showNotificationError}
          setOpen={toggleNotificationError}
          title={textNotificationError}
          type="error"
        />
      </div>
    </section>
  );
}

PropertyDetail.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  status: PropTypes.string,
  mutateData: PropTypes.func,
  toggleModal: PropTypes.func,
  publishedProperties: PropTypes.number,
  activateProperty: PropTypes.func,
};
